// Volume drag policy — pure helpers, no React, so the awkward parts can
// be unit-tested instead of eyeballed on a phone. Sibling in spirit to
// dev/cover-motion.test.mjs and dev/intent-settle.test.mjs.
//
// The problem these solve: `Slider` fires onChange on every pointermove
// (~60/s), and VolumeBar wired that straight to a volume service call
// while rendering its thumb from live HA state. Two consequences, both
// visible on a phone and worse off-LAN:
//
//   1. Flood. One drag is hundreds of calls. In a group it is worse —
//      onGroupVolume fans out one call PER MEMBER, so a five-speaker
//      group is ~300 calls/second of drag.
//   2. Fight. The thumb renders from the echo, which lags the finger,
//      so the control snaps backwards under the user mid-drag.
//
// Same intent-layer shape the lights / shades / climate controls already
// use: hold an optimistic local value, rate-limit what goes out, and
// suppress the echo until it agrees with what we last asked for.

// Minimum gap between outbound sends during a drag. 200ms is the value
// the TODO scoped: fast enough that the speaker tracks the finger
// audibly, slow enough that a 3-second drag is ~15 calls not ~180.
export const SEND_INTERVAL_MS = 200;

// How long to keep showing the optimistic value after the finger lifts
// before giving up and deferring to HA again. Covers the round trip on
// a slow link; past this we would rather show the truth than lie.
export const SETTLE_TIMEOUT_MS = 1200;

// Volume points. Sonos rounds, and a 1-point disagreement is inaudible,
// so treating it as settled avoids holding the optimistic value open on
// a rounding difference that will never close.
export const SETTLE_TOLERANCE = 1;

// Rate limit. Returns true when a move at `now` should go out on the
// wire given when we last sent. The first move of a gesture always
// sends (lastSentAt === null), so the control feels immediate.
export function shouldSend(now, lastSentAt, interval = SEND_INTERVAL_MS) {
  if (lastSentAt == null) return true;
  return now - lastSentAt >= interval;
}

// Has the echo caught up with what we last asked for? Used to drop the
// optimistic value early rather than always waiting out the timeout.
export function isSettled(echoed, target, tolerance = SETTLE_TOLERANCE) {
  if (echoed == null || target == null) return false;
  return Math.abs(echoed - target) <= tolerance;
}

// Group volume, computed against a baseline captured at drag start
// rather than against live state on every move.
//
// This is the subtle one. The old onGroupVolume did
// `delta = newAvg - groupAvg` using the CURRENT average, then added
// that delta to each member's CURRENT volume. That is only correct if
// the echo has fully caught up between one move and the next. Under a
// drag it has not, so each move computes its delta from a stale average
// and the group overshoots — and throttling alone would not have fixed
// it, only made it less frequent.
//
// Capturing {entity, volume} once per gesture makes every send in that
// gesture absolute and idempotent: re-sending the same target twice is
// a no-op rather than a second push in the same direction.
export function groupTargets(baseline, baselineAvg, nextAvg) {
  const delta = nextAvg - baselineAvg;
  return baseline.map((m) => ({
    entity: m.entity,
    volume: Math.max(0, Math.min(100, Math.round((m.volume ?? 0) + delta))),
  }));
}

// Average helper — kept here so the baseline average and the live
// average are computed the same way and cannot drift apart.
export function averageVolume(members) {
  if (!members || members.length === 0) return 0;
  const sum = members.reduce((acc, m) => acc + (m.volume ?? 0), 0);
  return Math.round(sum / members.length);
}

// Ceiling for any amp volume the panel sends. On 2026-10-30 one touch on
// the Pavilion slider sent the DRX-3.1 from 59 to 93, which is painfully
// loud in that room; normal TV listening sits at 55-59 and the wake
// scripts land on 45-55. The amp's own remote can still go higher; the
// panel cannot. Sonos units are not capped: their level is not the
// audible one in the amp rooms, and elsewhere a Sonos at 100 is loud
// but not dangerous.
export const AMP_VOLUME_MAX = 75;

export const AMP_ENTITIES = new Set([
  'media_player.drx_3_1',
  'media_player.drx_3_1_zone_2',
  'media_player.drx_5',
  'media_player.drx_5_zone_3',
]);

// Clamp a 0-100 level for the entity it is going to. Every panel volume
// write for an arbitrary entity goes through sonosSpeakerCommand, which
// applies this, so the TV rocker, the music VolumeBar, the Tune member
// rows and the Pool extension rocker are all covered at one point.
export function capVolume(entity, level) {
  const v = Math.max(0, Math.min(100, Number(level)));
  return AMP_ENTITIES.has(entity) ? Math.min(v, AMP_VOLUME_MAX) : v;
}

// Relative drag for volume sliders. The shared Slider jumps the level to
// wherever the finger lands, so a touch near the right-hand end of the
// track was a single volume_set to that level (the 2026-10-30 Pavilion
// 59 → 93). In relative mode a touch alone sends nothing; the level only
// moves once the finger has travelled DRAG_DEAD_PX sideways, and then by
// the distance moved from where it started, not by where it is.
export const DRAG_DEAD_PX = 8;

export function dragEngaged(dx, deadPx = DRAG_DEAD_PX) {
  return Math.abs(dx) >= deadPx;
}

// The dead zone is subtracted so the level starts moving from where it
// was rather than jumping by the dead zone's worth on engagement.
export function relativeLevel({ startValue, dx, width, min = 0, max = 100, step = 1, deadPx = DRAG_DEAD_PX }) {
  if (!width) return startValue;
  const travel = Math.sign(dx) * Math.max(0, Math.abs(dx) - deadPx);
  const raw = startValue + (travel / width) * (max - min);
  const stepped = Math.round(raw / step) * step;
  return Math.max(min, Math.min(max, stepped));
}
